import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import { AText, Box } from '@alf/uikit';
import border from '../../assets/border-image.svg?url';
import center from '../../assets/star.svg?url';
import { CustomCSSProperties, WheelSegment } from '../../types';
import { defaultSegments, segmentContentMap } from './config';

import styles from './index.module.css';

type PropsType = {
  segments?: WheelSegment[];
  onFinished: (segment: WheelSegment) => void;
  setDisabled: (value: boolean) => void;
};

export const Wheel = forwardRef(
  ({ segments = [], onFinished, setDisabled }: PropsType, ref) => {
    const wheelRef = useRef<HTMLUListElement | null>(null);
    const animationRef = useRef<Animation | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const [isSpinning, setIsSpinning] = useState(false);

    // To place the first sector on north of wheel
    const [currentRotation] = useState(-90);

    const spinWheel = () => {
      if (isSpinning || !segments.length) {
        return;
      }

      setDisabled(true);
      setIsSpinning(true);

      animationRef.current =
        wheelRef.current?.animate(
          [
            { transform: `rotate(${currentRotation}deg)` },
            { transform: `rotate(${currentRotation + 360}deg)` }
          ],
          {
            duration: 500,
            easing: 'linear',
            fill: 'forwards',
            iterations: Infinity
          }
        ) || null;
    };

    const stopAndSpinToWinningSegment = (winningSegment: number | string) => {
      if (animationRef.current) {
        animationRef.current.cancel();
      }

      const totalSegments = segments.length;
      const segmentAngle = 360 / totalSegments;
      const additionalSpins = 6 * 360; // Add 3 full spins

      const winningIndex = segments.findIndex(
        (segment) => segment.id === winningSegment
      );

      const finalAngle = 360 - winningIndex * segmentAngle;
      const newRotation = currentRotation + additionalSpins + finalAngle;

      if (wheelRef.current) {
        wheelRef.current.animate(
          [
            { transform: `rotate(${currentRotation}deg)` },
            {
              transform: `rotate(${newRotation}deg)`
            }
          ],
          {
            easing: 'ease-out',
            duration: 4500,
            fill: 'forwards'
          }
        ).onfinish = () => {
          setIsSpinning(false);
          onFinished(segments[winningIndex]);
          setDisabled(false);
        };
      }
    };

    const emergencyStop = () => {
      setIsSpinning(false);
      setDisabled(true);

      if (animationRef.current) {
        animationRef.current.cancel();
      }
    };

    useImperativeHandle(ref, () => ({
      spinWheel,
      stopAndSpinToWinningSegment,
      emergencyStop,
      height: containerRef.current?.getBoundingClientRect().height
    }));

    const renderSegments = () => {
      return (segments.length ? segments : defaultSegments).map(
        ({ id, value, type }, index) => {
          const SegmentContent = segmentContentMap[type];

          return (
            <Box
              key={id}
              component="li"
              className={styles.segment}
              style={{ '--idx': index } as CustomCSSProperties}
            >
              <SegmentContent value={value} />
            </Box>
          );
        }
      );
    };

    return (
      <Box
        ref={containerRef}
        className={styles.wheelWrapper}
        style={
          {
            '--segment-count': segments.length
              ? segments.length
              : defaultSegments.length
          } as CustomCSSProperties
        }
      >
        <img src={center} alt="" className={styles.centralStar} />
        <img src={border} alt="" className={styles.borderImage} />
        <Box className={styles.shadowContainer} />

        <Box align="center" justify="center" className={styles.center}>
          <AText
            breed="BU"
            onClick={ref ? undefined : spinWheel}
            className={styles.centerText}
          >
            Spin
          </AText>
        </Box>

        <Box component="ul" ref={wheelRef} className={styles.segmentsContainer}>
          {renderSegments()}
        </Box>
      </Box>
    );
  }
);
